import { AsyncStatus } from 'lib-shared-types-api';
import type { ConversationsIdsBulk } from 'lib-shared-types-api';
import { useCallback, useMemo, useState } from 'react';
import { useDebounce, useList, useUpdateEffect } from 'react-use';

type UseSelectConversationsResult = {
  /** The async status of the read conversations api endpoint. */
  readonly asyncStatus: AsyncStatus;
  /** The ids of the eligible conversation for bulk actions. */
  readonly eligibleBulkConversationIds: readonly number[];
  /** The total amount of filtered conversations. */
  readonly totalFilteredConversations: number;
  /** Whether all conversations on current page are selected. */
  readonly isCurrentPageAllConversationsSelected: boolean;
  /** Whether some conversations on current page are selected. */
  readonly isCurrentPageSomeConversationsSelected: boolean;
  /** The ids of the selected conversations. */
  readonly selectedConversationIds: readonly number[];
  /** The method for clearing selected conversations. */
  readonly clearSelectedConversations: () => void;
  /** The method for selecting a conversation. */
  readonly selectConversation: (conversationId: number) => void;
  /** The method for de-selecting a conversation. */
  readonly deselectConversation: (conversationId: number) => void;
  /** The method for toggling the selection state of a conversation. */
  readonly toggleSelectConversation: (conversationId: number) => void;
  /** The method for selecting all conversations on a page. */
  readonly selectAllCurrentPageConversation: () => void;
  /** The method for selecting all eligible conversations for bulk actions. */
  readonly selectAllEligibleConversations: () => void;
  /** The method for resetting the current selection and other states. */
  readonly resetSelection: () => void;
};

type UseSelectConversationsOptions = {
  /** The conversation ids of the current page. */
  readonly currentPageConversationIds: readonly number[];
  /** The promise to wait for to get the bulk ids of the conversations. */
  readonly onFetchConversationIdsBulk: () => Promise<ConversationsIdsBulk>;
};

/** A generic hook that handles selecting conversations across various views. */
export const useSelectConversations = ({
  currentPageConversationIds,
  onFetchConversationIdsBulk,
}: UseSelectConversationsOptions): UseSelectConversationsResult => {
  // Represents selected conversations across multiple pages.
  const [selectedConversationIds, selectedConversationIdsSetters] =
    useList<number>([]);
  // Represents ids of conversations eligible for bulk selection.
  const [eligibleBulkConversationIds, eligibleBulkConversationIdsSetters] =
    useList<number>([]);

  // Represents the count of filtered conversations. This essentially is used by
  // the consumer in combination with eligible bulk conversations list and
  // selected conversations to determine which and how many conversations the
  // user is allowed to perform bulk actions on.
  const [totalFilteredConversations, setTotalFilteredConversations] =
    useState(0);
  const [asyncStatus, setAsyncStatus] = useState<AsyncStatus>('uninitialized');

  const {
    currentPageUnselectedConversationIds,
    isCurrentPageAllConversationsSelected,
    isCurrentPageSomeConversationsSelected,
  } = useMemo(() => {
    const currentPageSelectedConversationIds: readonly number[] =
      selectedConversationIds.filter((id) =>
        currentPageConversationIds.includes(id),
      );

    const currentPageUnselectedConversationIds: readonly number[] =
      currentPageConversationIds.filter(
        (id) => !currentPageSelectedConversationIds.includes(id),
      );

    const isCurrentPageAllConversationsSelected =
      currentPageConversationIds.length ===
      currentPageSelectedConversationIds.length;

    const isCurrentPageSomeConversationsSelected =
      !!currentPageSelectedConversationIds.length &&
      currentPageConversationIds.length >
        currentPageSelectedConversationIds.length;

    return {
      currentPageUnselectedConversationIds,
      isCurrentPageAllConversationsSelected,
      isCurrentPageSomeConversationsSelected,
    };
  }, [currentPageConversationIds, selectedConversationIds]);

  /** Whether there is a selection of one or more conversations. */
  const hasSelection = selectedConversationIds.length > 0;

  /**
   * Handles adding a conversation id to the selected conversation ids.
   *
   * @param conversationId The conversation id to select.
   */
  const selectConversation = useCallback(
    (conversationId: number) =>
      selectedConversationIdsSetters.push(conversationId),
    [selectedConversationIdsSetters],
  );

  /**
   * Handles removing a conversation id from the selected conversation ids.
   *
   * @param conversationId The conversation id to deselect.
   */
  const deselectConversation = useCallback(
    (conversationId: number) =>
      selectedConversationIdsSetters.filter((id) => id !== conversationId),

    [selectedConversationIdsSetters],
  );

  /**
   * Handles add/remove of conversation id to/from selected conversation ids.
   *
   * @param conversationId The conversation id to select/deselect.
   */
  const toggleSelectConversation = useCallback(
    (conversationId: number) =>
      selectedConversationIds.includes(conversationId)
        ? deselectConversation(conversationId)
        : selectConversation(conversationId),
    [deselectConversation, selectConversation, selectedConversationIds],
  );

  /** Handles selecting all conversations on the current page. */
  const selectAllCurrentPageConversation = useCallback(
    () =>
      isCurrentPageAllConversationsSelected
        ? selectedConversationIdsSetters.filter(
            (id) => !currentPageConversationIds.includes(id),
          )
        : selectedConversationIdsSetters.push(
            ...currentPageUnselectedConversationIds,
          ),
    [
      currentPageConversationIds,
      currentPageUnselectedConversationIds,
      isCurrentPageAllConversationsSelected,
      selectedConversationIdsSetters,
    ],
  );

  /** Handles selecting all eligible conversations for bulk actions. */
  const selectAllEligibleConversations = useCallback(() => {
    if (eligibleBulkConversationIds.length) {
      selectedConversationIdsSetters.set(eligibleBulkConversationIds);
    }
  }, [eligibleBulkConversationIds, selectedConversationIdsSetters]);

  /** Handles clearing all selected conversations. */
  const clearSelectedConversations = useCallback(
    () => selectedConversationIdsSetters.clear(),
    [selectedConversationIdsSetters],
  );

  // Handles fetching the conversations bulk ids. This happens on the first
  // selection. We debounce as fetching seems to cause minor rendering delays.
  const [_, cancelFetchingBulkIds] = useDebounce(
    async () => {
      if (!hasSelection) {
        return;
      }

      try {
        setAsyncStatus('pending');

        const { csids, total_conversations } =
          await onFetchConversationIdsBulk();

        eligibleBulkConversationIdsSetters.set(csids as number[]);
        setTotalFilteredConversations(total_conversations);
        setAsyncStatus('fulfilled');
      } catch {
        setAsyncStatus('rejected');
      }
    },
    hasSelection ? 300 : 0,
    [hasSelection],
  );

  /** Resets various hook related states. */
  const resetSelection = useCallback(() => {
    setAsyncStatus('uninitialized');
    cancelFetchingBulkIds();
    clearSelectedConversations();
    eligibleBulkConversationIdsSetters.clear();
    setTotalFilteredConversations(0);
  }, [
    cancelFetchingBulkIds,
    clearSelectedConversations,
    eligibleBulkConversationIdsSetters,
  ]);

  // Handles when there's no more selection.
  useUpdateEffect(() => {
    if (!hasSelection) {
      resetSelection();
    }
  }, [cancelFetchingBulkIds, clearSelectedConversations, hasSelection]);

  return {
    asyncStatus,
    eligibleBulkConversationIds,
    isCurrentPageAllConversationsSelected,
    isCurrentPageSomeConversationsSelected,
    selectedConversationIds,
    totalFilteredConversations,
    clearSelectedConversations,
    selectAllCurrentPageConversation,
    selectAllEligibleConversations,
    selectConversation,
    deselectConversation,
    toggleSelectConversation,
    resetSelection,
  };
};
